import { Colors } from "@/enums/Colors";
import { Box, BoxProps, SxProps, Theme, Typography, styled } from "@mui/material";
import StarIconSVG from "../../../../assets/images/star-logo.svg";

interface Props extends BoxProps {
  sxClass?: SxProps<Theme>;
  containerSxClass?: SxProps<Theme>;
  staticPurple?: boolean;
  displayText?: boolean;
  disableHoverEffect?: boolean;
  disableWidthAnimation?: boolean;
}

/**
 * Star AI Icon
 */
export const StarIcon = ({
  sxClass,
  containerSxClass,
  staticPurple,
  displayText = true,
  disableHoverEffect,
  disableWidthAnimation,
  ...props
}: Props) => {
  return (
    <StyledContainer
      disableWidthAnimation={disableWidthAnimation}
      disableHoverEffect={!!disableHoverEffect}
      sx={{
        backgroundColor: !staticPurple ? Colors.BUTTON_SECONDARY_PURPLE : Colors.BUTTON_PURPLE,
        ...containerSxClass,
      }}
      {...props}
    >
      {staticPurple ? (
        <StaticPurple sx={sxClass} src={StarIconSVG.src} alt="lazo-icon" />
      ) : (
        <StyledImage sx={sxClass} src={StarIconSVG.src} alt="lazo-icon" />
      )}
      {displayText && <Typography>Ask AI</Typography>}
    </StyledContainer>
  );
};

const StaticPurple = styled("img")({
  filter: "brightness(100)",
});

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "disableHoverEffect",
})(({ disableHoverEffect, disableWidthAnimation }: { disableHoverEffect: boolean; disableWidthAnimation: boolean }) => ({
  width: "1.8rem",
  height: "1.8rem",
  display: "flex",
  padding: "0.25rem",
  textAlign: "center",
  alignItems: "center",
  transition: "all 0.1s ease",
  justifyContent: "space-between",
  overflow: "hidden",
  color: Colors.WHITE,
  textWrap: "nowrap",
  "&.MuiBox-root": {
    borderRadius: "50% !important",
  },
  "& .MuiTypography-root": {
    visibility: "hidden",
    opacity: 0,
    fontWeight: "500",
  },

  ...(disableHoverEffect
    ? { cursor: "pointer" }
    : {
        ":hover": {
          borderRadius: "2rem 2rem 2rem 2rem !important",
          cursor: "pointer",
          backgroundColor: Colors.LAZO_PURPLE,
          ...(!disableWidthAnimation && {
            width: "4.5rem",
          }),
          img: {
            filter: "brightness(100)",
          },
          "& .MuiTypography-root": {
            visibility: "visible",
            opacity: 1,
          },
        },
      }),
}));

interface StaticStarIconProps extends BoxProps {
  iconSxClass?: SxProps<Theme>;
}

export const StaticStarIcon = ({ iconSxClass, ...props }: StaticStarIconProps) => {
  return (
    <Container {...props} className="static-star-icon">
      <StyledImage sx={iconSxClass} src={StarIconSVG.src} alt="lazo-icon" />
    </Container>
  );
};

const Container = styled(Box)({
  "&.static-star-icon": {
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
  },
});

const StyledImage = styled("img")({});
